import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import type { MaterialNodeSpec } from '@/graph/domain/types';
import { sectionHeaderSx, sectionBoxSx, labelValueRowSx, labelSx, valueSx } from './info.styles';

interface MaterialInfoProps {
  data: MaterialNodeSpec & { id: string };
}

const buildInfoConstituentExpr = (constituents: MaterialNodeSpec['constituents']): string => {
  if (!constituents || constituents.length === 0) return '';
  return `(${constituents.map((c) => c.name).join(' + ')})`;
};

export const MaterialInfo: React.FC<MaterialInfoProps> = ({ data }) => {
  const stateExpr = buildInfoConstituentExpr(data.constituents);
  const qtyDisplay = data.quantity && data.quantity.value.amount != null && data.quantity.value.units
    ? `${data.quantity.value.amount} ${data.quantity.value.units}`
    : '—';

  // Format volume display
  const volumeDisplay = (() => {
    const volume = data.container.volume;
    if (volume && volume.amount != null && volume.unit) {
      return `${volume.amount} ${volume.unit}`;
    }
    const wellVolume = data.container.wellVolume;
    if (wellVolume && wellVolume.amount != null && wellVolume.units) {
      return `${wellVolume.amount} ${wellVolume.units}`;
    }
    return '—';
  })();

  // Format address display
  const addressDisplay = data.container.wellAddresses
    ? data.container.wellAddresses.map((a) => a.name).join(', ') || '—'
    : '—';

  return (
    <Box>
      {/* Container Section */}
      <Typography sx={sectionHeaderSx}>Container</Typography>
      <Box sx={sectionBoxSx}>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Name</Typography>
          <Typography sx={valueSx}>{data.container.name}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Type</Typography>
          <Typography sx={valueSx}>{data.container.containerType}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Volume</Typography>
          <Typography sx={valueSx}>{volumeDisplay}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Address</Typography>
          <Typography sx={valueSx}>{addressDisplay}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Vendor</Typography>
          <Typography sx={valueSx}>{data.container.make || '—'}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* Material Section */}
      <Typography sx={sectionHeaderSx}>Material</Typography>
      <Box sx={sectionBoxSx}>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Name</Typography>
          <Typography sx={valueSx}>{data.name}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Constituent Materials</Typography>
          <Typography sx={valueSx}>{data.constituents.length > 0 ? stateExpr : '—'}</Typography>
        </Box>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Unit Type</Typography>
          <Typography sx={valueSx}>{data.quantity?.type ?? '—'}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* Quantity Section */}
      <Typography sx={sectionHeaderSx}>Quantity</Typography>
      <Box sx={sectionBoxSx}>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Initial</Typography>
          <Typography sx={valueSx}>{qtyDisplay}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* Notes */}
      {data.notes && (
        <>
          <Divider />
          <Typography sx={sectionHeaderSx}>Notes</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {data.notes}
          </Typography>
        </>
      )}
    </Box>
  );
};
